import { useState, useCallback } from 'react';

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  email?: boolean;
  phone?: boolean;
  password?: boolean;
  match?: string; // Field name to match with
  custom?: (value: string) => string | null; // Custom validation function
}

export interface ValidationRules {
  [fieldName: string]: ValidationRule;
}

export interface ValidationErrors {
  [fieldName: string]: string;
}

export interface FormValidationHook {
  errors: ValidationErrors;
  validateField: (fieldName: string, value: string, allValues?: Record<string, string>) => string | null;
  validateForm: (values: Record<string, string>) => boolean;
  clearErrors: () => void;
  clearFieldError: (fieldName: string) => void;
}

export const useFormValidation = (rules: ValidationRules): FormValidationHook => {
  const [errors, setErrors] = useState<ValidationErrors>({});

  const validateField = useCallback((
    fieldName: string, 
    value: string, 
    allValues?: Record<string, string>
  ): string | null => {
    const rule = rules[fieldName];
    if (!rule) return null;

    // Required validation
    if (rule.required && (!value || value.trim() === '')) {
      return 'This field is required';
    }

    // Skip other validations if field is empty and not required
    if (!value || value.trim() === '') {
      return null;
    }

    // Length validations
    if (rule.minLength && value.length < rule.minLength) {
      return `Minimum ${rule.minLength} characters required`;
    }

    if (rule.maxLength && value.length > rule.maxLength) {
      return `Maximum ${rule.maxLength} characters allowed`;
    }

    // Pattern validation
    if (rule.pattern && !rule.pattern.test(value)) {
      return 'Invalid format';
    }

    // Email validation
    if (rule.email) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(value)) {
        return 'Please enter a valid email address';
      }
    }

    // Phone validation (basic international format)
    if (rule.phone) {
      const phonePattern = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/;
      if (!phonePattern.test(value)) {
        return 'Please enter a valid phone number';
      }
    }

    // Password validation
    if (rule.password) {
      if (value.length < 8) {
        return 'Password must be at least 8 characters long';
      }
      if (!/(?=.*[a-z])/.test(value)) {
        return 'Password must contain at least one lowercase letter';
      }
      if (!/(?=.*[A-Z])/.test(value)) {
        return 'Password must contain at least one uppercase letter';
      }
      if (!/(?=.*\d)/.test(value)) {
        return 'Password must contain at least one number';
      }
    }

    // Field matching (for confirm password, etc.)
    if (rule.match && allValues) {
      const matchValue = allValues[rule.match];
      if (value !== matchValue) {
        return `This field must match ${rule.match}`;
      }
    }

    // Custom validation
    if (rule.custom) {
      const customError = rule.custom(value);
      if (customError) return customError;
    }

    return null;
  }, [rules]);

  const validateForm = useCallback((values: Record<string, string>): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    Object.keys(rules).forEach(fieldName => {
      const error = validateField(fieldName, values[fieldName], values);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [rules, validateField]);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const clearFieldError = useCallback((fieldName: string) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  return {
    errors,
    validateField,
    validateForm,
    clearErrors,
    clearFieldError,
  };
};

// Predefined validation rules for common fields
export const commonValidationRules = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
  },
  email: {
    required: true,
    email: true,
    maxLength: 255,
  },
  phone: {
    required: true,
    phone: true,
    maxLength: 20,
  },
  password: {
    required: true,
    password: true,
    maxLength: 255,
  },
  password_confirmation: {
    required: true,
    match: 'password',
    maxLength: 255,
  },
  companyName: {
    required: true,
    minLength: 2,
    maxLength: 255,
  },
  address: {
    maxLength: 500,
  },
  licenseNumber: {
    maxLength: 50,
  },
  taxId: {
    maxLength: 50,
  },
  description: {
    maxLength: 1000,
  },
};
